import { Spinner } from '@Components/common/Spinner';
import { _ApplyPaymentMethod, _CheckoutToOrder, _CheckoutToPayment } from '@Services/Checkout';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, RootState } from 'store';
import { PaymentMethods } from 'types/Checkout';
import PaymentMethodCard from '../PaymentMethodCard';
import {
  MainCollapseAction,
  MainCollapseContent,
  MainCollapseFooter,
  MainCollapseHeader,
  MainCollapseWrapper,
  PaymentMethodsWrapper,
  Step,
  SubCollapseCheck,
  SubCollapseContent,
  SubCollapseHeader,
  SubCollapseHeaderInfo,
  SubCollapseHeaderInner,
  SubCollapseIcon,
  SubCollapseTitle,
  SubCollapseWrapper,
} from './CheckoutSteps.styled';

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAddressCollapseIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const CollipsePaymentMethod = ({
  paymentKey,
  isLoading,
  setIsLoading,
}: {
  paymentKey: keyof PaymentMethods;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation('Checkout');
  const { data, paymentMethods } = useSelector((state: RootState) => state.checkout);
  const { checkout: checkoutModel } = useDispatch<Dispatch>();
  const applyPaymentMethodHandler = useCallback(
    async (paymentMethod: string) => {
      setIsLoading(true);
      const newCheckoutData = await _ApplyPaymentMethod(paymentMethod);
      checkoutModel.setCheckoutData(newCheckoutData);
      setIsLoading(false);
    },
    [checkoutModel, setIsLoading]
  );
  const [isOptionsOpened, setIsOptionsOpened] = useState(false);

  const handleOnClick = () => {
    if (['CASH_ON_DELIVERY', 'credit_card_payment'].includes(paymentKey)) {
      if (paymentMethods?.[paymentKey]?.[0]) {
        applyPaymentMethodHandler(paymentMethods[paymentKey][0]);
      }
    } else {
      setIsOptionsOpened((prev) => !prev);
    }
  };

  const handleSelectPaymentMethod = async (paymentMethodKey: string) => {
    await applyPaymentMethodHandler(paymentMethodKey);
  };

  return (
    <SubCollapseWrapper>
      <SubCollapseHeader
        isActive={Boolean(
          paymentMethods &&
            paymentMethods[paymentKey].some((payment) => data?.payment_method && payment === data?.payment_method)
        )}
        isDisabled={isLoading}
        onClick={handleOnClick}
      >
        <SubCollapseCheck />
        <SubCollapseHeaderInner>
          <SubCollapseIcon
            icon={
              paymentKey === 'CASH_ON_DELIVERY'
                ? 'cash.svg'
                : paymentKey === 'credit_card_payment'
                ? 'visa.svg'
                : paymentKey === 'instalment_payment'
                ? 'dollar.svg'
                : paymentKey === 'E_WALLET'
                ? 'payment.svg'
                : 'payment.svg'
            }
          />
          <SubCollapseHeaderInfo>
            {t(
              paymentKey === 'CASH_ON_DELIVERY'
                ? 'cash-on-delivery'
                : paymentKey === 'credit_card_payment'
                ? 'online-payment'
                : paymentKey === 'instalment_payment'
                ? 'installment-payment'
                : paymentKey === 'E_WALLET'
                ? 'e-wallet'
                : 'other_payment'
            )}
          </SubCollapseHeaderInfo>
        </SubCollapseHeaderInner>
      </SubCollapseHeader>
      {['instalment_payment', 'E_WALLET', 'other_payment'].includes(paymentKey) && (
        <UnmountClosed isOpened={isOptionsOpened}>
          <SubCollapseContent>
            <SubCollapseTitle>{t('select_payment_method')}</SubCollapseTitle>
            {paymentMethods && (
              <PaymentMethodsWrapper>
                {(
                  paymentMethods[paymentKey] as
                    | PaymentMethods['instalment_payment']
                    | PaymentMethods['E_WALLET']
                    | PaymentMethods['other_payment']
                ).map((theKey) => (
                  <PaymentMethodCard
                    key={theKey}
                    paymentMethod={theKey}
                    isDisabled={isLoading}
                    isSelected={data?.payment_method === theKey}
                    onClick={handleSelectPaymentMethod}
                  />
                ))}
              </PaymentMethodsWrapper>
            )}
          </SubCollapseContent>
        </UnmountClosed>
      )}
    </SubCollapseWrapper>
  );
};

export default function PaymentCollapse({ isOpen, setIsOpen, setAddressCollapseIsOpen }: Props) {
  const { t } = useTranslation('Checkout');
  const { data, paymentMethods } = useSelector((state: RootState) => state.checkout);
  const { cart } = useDispatch<Dispatch>();
  const { push, query } = useRouter();
  const hasThirdpartyParam = query.hasOwnProperty('thirdparty');
  const isAddressEnabled = useMemo(
    () => Boolean(data?.checkout_items && data?.checkout_items.some((item) => !Boolean(item.need_pickup))),
    [data?.checkout_items]
  );
  const isPickupEnabled = useMemo(
    () => data?.checkout_items && data?.checkout_items.some((item) => Boolean(item.need_pickup)),
    [data?.checkout_items]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [submitOrderLoading, setSubmitOrderLoading] = useState(false);

  const handlePayment = () => {
    setSubmitOrderLoading(true);
    if (data?.payment_method === 'CASH_ON_DELIVERY') {
      _CheckoutToOrder()
        .then((data) => {
          push(`/checkout/payment_status?success=true${hasThirdpartyParam ? '&thirdparty=true' : ''}`).then(() => {
            cart.getCartList();
          });
        })
        .catch((err) => {
          console.log(err);
          setSubmitOrderLoading(false);
        });
    } else {
      _CheckoutToOrder()
        .then((data) => {
          _CheckoutToPayment()
            .then((data) => {
              window.location.href = data.iframe;
            })
            .catch((err) => {
              console.log(err);
              setSubmitOrderLoading(false);
            });
        })
        .catch((err) => {
          console.log(err);
          setSubmitOrderLoading(false);
        });
    }
  };

  const hardCodedPaymentMethodsKeys =
    (paymentMethods && (Object.keys(paymentMethods) as Array<keyof typeof paymentMethods>)) || [];

  return (
    <MainCollapseWrapper isActive={isOpen}>
      <MainCollapseHeader
        active={isOpen}
        onClick={
          (isAddressEnabled ? Boolean(data?.address) : true) && (isPickupEnabled ? Boolean(data?.pick_point) : true)
            ? () => {
                setIsOpen((prev) => !prev);
                setAddressCollapseIsOpen(false);
              }
            : undefined
        }
      >
        {/* {hardCodedPaymentMethodsKeys.map((paymentMethod) => {
          return <h1 key={paymentMethod}>{paymentMethod}</h1>;
        })} */}
        <Step>2</Step> {t('payment_method')}
      </MainCollapseHeader>
      <UnmountClosed isOpened={isOpen}>
        <MainCollapseContent>
          {hardCodedPaymentMethodsKeys.map((key: keyof PaymentMethods) => (
            <CollipsePaymentMethod key={key} paymentKey={key} isLoading={isLoading} setIsLoading={setIsLoading} />
          ))}
          <MainCollapseFooter>
            <MainCollapseAction
              variant="filled-red"
              type="button"
              disabled={!Boolean(data?.payment_method) || submitOrderLoading}
              onClick={Boolean(data?.payment_method) ? handlePayment : undefined}
            >
              {submitOrderLoading ? <Spinner /> : t('complete-order')}
            </MainCollapseAction>
          </MainCollapseFooter>
        </MainCollapseContent>
      </UnmountClosed>
    </MainCollapseWrapper>
  );
}
